import { apiSlice } from '../../apiSlice';

export const offerApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    // Mutation for a user to submit a custom rental offer
    submitRentalOffer: builder.mutation({
      query: (offerData) => ({
        url: '/offers',
        method: 'POST',
        body: {
          ...offerData,
          type: 'rent', // Ensure type is set to rent for these offers
        },
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'Offer', id: 'LIST' },
        { type: 'Product', id: arg.product }, // Invalidate the specific product
      ],
    }),

    // Query for a user to get their submitted rental offers
    getMyRentalOffers: builder.query({
      query: (params) => ({
        url: '/offers',
        params: { ...params, type: 'rent' }, // Filter by type: 'rent' and other user-specific filters if needed by backend
      }),
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: 'Offer', id })), { type: 'Offer', id: 'LIST' }]
          : [{ type: 'Offer', id: 'LIST' }],
    }),

    // Query for a merchant to get rental offers received for their products
    getMerchantRentalOffers: builder.query({
      query: (params) => ({
        url: '/offers', // Backend needs to filter these for the logged-in merchant
        params: { ...params, type: 'rent' }, // Filter by type: 'rent'
      }),
      providesTags: (result) =>
        result
          ? [...result.map(({ id }) => ({ type: 'Offer', id })), { type: 'Offer', id: 'LIST' }]
          : [{ type: 'Offer', id: 'LIST' }],
    }),

    // Mutation for a merchant to accept a rental offer
    acceptRentalOffer: builder.mutation({
      query: (offerId) => ({
        url: `/offers/${offerId}`,
        method: 'PUT',
        body: { status: 'accepted' },
      }),
      invalidatesTags: (result, error, offerId) => [{ type: 'Offer', id: offerId }, { type: 'Offer', id: 'LIST' }],
    }),

    // Mutation for a merchant to reject a rental offer
    rejectRentalOffer: builder.mutation({
      query: (offerId) => ({
        url: `/offers/${offerId}`,
        method: 'PUT',
        body: { status: 'rejected' },
      }),
      invalidatesTags: (result, error, offerId) => [{ type: 'Offer', id: offerId }, { type: 'Offer', id: 'LIST' }],
    }),

    // General query to get a specific offer by ID (if needed)
    getOfferById: builder.query({
        query: (offerId) => `/offers/${offerId}`,
        providesTags: (result, error, offerId) => [{ type: 'Offer', id: offerId }],
    }),

    // Submit a custom request (custom rental offer or made-to-order commission)
    submitCustomRequest: builder.mutation({
      query: (requestData) => ({
        url: '/offers',
        method: 'POST',
        body: requestData,
      }),
      invalidatesTags: (result, error, arg) => [
        { type: 'Offer', id: 'LIST' },
        { type: 'Product', id: arg.product },
      ],
    }),

    prepareOfferCheckout: builder.mutation({
      query: (data) => ({
        url: '/payments/prepare-offer-checkout',
        method: 'POST',
        body: data,
      }),
    }),

    acceptOffer: builder.mutation({
      query: (offerId) => ({
        url: `/offers/${offerId}/accept`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, offerId) => [{ type: 'Offer', id: offerId }, { type: 'Offer', id: 'LIST' }],
    }),

    rejectOffer: builder.mutation({
      query: (offerId) => ({
        url: `/offers/${offerId}/reject`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, offerId) => [{ type: 'Offer', id: offerId }, { type: 'Offer', id: 'LIST' }],
    }),

    counterOffer: builder.mutation({
      query: ({ offerId, newPrice, message }) => ({
        url: `/offers/${offerId}/counter`,
        method: 'POST',
        body: { newPrice, message },
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'Offer', id: arg.offerId }, { type: 'Offer', id: 'LIST' }],
    }),

    acceptCounterOffer: builder.mutation({
      query: ({ offerId, paymentIntentId }) => ({
        url: `/offers/${offerId}/accept-counter`,
        method: 'POST',
        body: { paymentIntentId },
      }),
      invalidatesTags: (result, error, arg) => [{ type: 'Offer', id: arg.offerId }, { type: 'Offer', id: 'LIST' }],
    }),

  }),
});

export const {
  useSubmitRentalOfferMutation,
  useGetMyRentalOffersQuery,
  useGetMerchantRentalOffersQuery,
  useAcceptRentalOfferMutation,
  useRejectRentalOfferMutation,
  useGetOfferByIdQuery,
  useSubmitCustomRequestMutation,
  usePrepareOfferCheckoutMutation,
  useAcceptOfferMutation,
  useRejectOfferMutation,
  useCounterOfferMutation,
  useAcceptCounterOfferMutation,
} = offerApi; 